import React, { createContext, useContext, useState, useEffect, type ReactNode } from 'react';

import { useMerchant } from '@contexts/admin/MerchantContext';

/**
 * Onboarding Context
 *
 * 用途：管理新手引導的狀態和流程
 * - 檢查是否為新使用者
 * - 管理引導步驟和進度
 * - 記錄完成狀態到 localStorage
 */

interface OnboardingContextValue {
  showTutorialSelection: boolean;
  showOnboarding: boolean;
  selectedTutorial: string | null;
  currentStep: number;
  loading: boolean;
  startTutorial: (tutorialKey: string) => void;
  skipAllTutorials: () => void;
  completeOnboarding: () => void;
  skipOnboarding: () => void;
  resetOnboarding: () => void;
  openTutorialSelection: () => void;
  nextStep: () => void;
  prevStep: () => void;
  goToStep: (step: number) => void;
  showQuickStart: boolean;
  hideQuickStart: () => void;
  displayQuickStart: () => void;
  completedTasks: string[];
  markTaskCompleted: (taskKey: string) => void;
  markTaskCompletedAndNext: (taskKey: string) => void;
  clearCompletedTasks: () => void;
}

const OnboardingContext = createContext<OnboardingContextValue | null>(null);

const ONBOARDING_STORAGE_KEY = 'gowarehouse_onboarding_completed';
const QUICK_START_STORAGE_KEY = 'gowarehouse_quick_start_show';
const COMPLETED_TASKS_STORAGE_KEY = 'gowarehouse_completed_tasks';

interface OnboardingProviderProps {
  children: ReactNode;
}

export const OnboardingProvider = ({ children }: OnboardingProviderProps) => {
  const { currentMerchant, loading: merchantLoading } = useMerchant();
  const [showTutorialSelection, setShowTutorialSelection] = useState(false); // 顯示教學選擇畫面
  const [showOnboarding, setShowOnboarding] = useState(false); // 顯示 Drawer
  const [selectedTutorial, setSelectedTutorial] = useState<string | null>(null); // 選擇的教學類型
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showQuickStart, setShowQuickStart] = useState(true);
  const [completedTasks, setCompletedTasks] = useState<string[]>([]);

  // 檢查是否已完成新手引導
  useEffect(() => {
    if (!merchantLoading && currentMerchant) {
      const storageKey = `${ONBOARDING_STORAGE_KEY}_merchant_${currentMerchant.id}`;
      const quickStartKey = `${QUICK_START_STORAGE_KEY}_merchant_${currentMerchant.id}`;
      const tasksKey = `${COMPLETED_TASKS_STORAGE_KEY}_merchant_${currentMerchant.id}`;

      const completed = localStorage.getItem(storageKey);
      const quickStartShow = localStorage.getItem(quickStartKey);
      const tasksData = localStorage.getItem(tasksKey);

      if (!completed) {
        // 新使用者或尚未完成引導 - 顯示教學選擇畫面
        setShowTutorialSelection(true);
      }

      // 載入快速開始顯示狀態（預設顯示）
      setShowQuickStart(quickStartShow !== 'false');

      // 載入已完成任務
      if (tasksData) {
        try {
          setCompletedTasks(JSON.parse(tasksData));
        } catch (e) {
          setCompletedTasks([]);
        }
      }

      setLoading(false);
    }
  }, [merchantLoading, currentMerchant]);

  // 開始特定教學
  const startTutorial = (tutorialKey: string) => {
    setSelectedTutorial(tutorialKey);
    setShowTutorialSelection(false); // 關閉選擇畫面
    setShowOnboarding(true); // 開啟 Drawer
    setCurrentStep(0);
  };

  // 跳過所有教學（從選擇畫面）
  const skipAllTutorials = () => {
    if (currentMerchant) {
      const storageKey = `${ONBOARDING_STORAGE_KEY}_merchant_${currentMerchant.id}`;
      localStorage.setItem(storageKey, 'true');
      setShowTutorialSelection(false);
      setShowOnboarding(false);
    }
  };

  // 完成新手引導
  const completeOnboarding = () => {
    if (currentMerchant) {
      const storageKey = `${ONBOARDING_STORAGE_KEY}_merchant_${currentMerchant.id}`;
      localStorage.setItem(storageKey, 'true');
      setShowOnboarding(false);
      setCurrentStep(0);
      setSelectedTutorial(null);
    }
  };

  // 跳過當前教學（從 Drawer）
  const skipOnboarding = () => {
    setShowOnboarding(false);
    setCurrentStep(0);
    setSelectedTutorial(null);
    // 不標記為完成，下次還會顯示選擇畫面
  };

  // 重置新手引導（用於測試或重新觀看）
  const resetOnboarding = () => {
    if (currentMerchant) {
      const storageKey = `${ONBOARDING_STORAGE_KEY}_merchant_${currentMerchant.id}`;
      localStorage.removeItem(storageKey);
      setShowTutorialSelection(true); // 顯示選擇畫面
      setShowOnboarding(false);
      setCurrentStep(0);
      setSelectedTutorial(null);
    }
  };

  // 打開教學選擇畫面
  const openTutorialSelection = () => {
    setShowTutorialSelection(true);
  };

  // 下一步
  const nextStep = () => {
    setCurrentStep(prev => prev + 1);
  };

  // 上一步
  const prevStep = () => {
    setCurrentStep(prev => Math.max(0, prev - 1));
  };

  // 跳到特定步驟
  const goToStep = (step: number) => {
    setCurrentStep(step);
  };

  // 隱藏快速開始卡片
  const hideQuickStart = () => {
    if (currentMerchant) {
      const quickStartKey = `${QUICK_START_STORAGE_KEY}_merchant_${currentMerchant.id}`;
      localStorage.setItem(quickStartKey, 'false');
      setShowQuickStart(false);
    }
  };

  // 顯示快速開始卡片
  const displayQuickStart = () => {
    if (currentMerchant) {
      const quickStartKey = `${QUICK_START_STORAGE_KEY}_merchant_${currentMerchant.id}`;
      localStorage.setItem(quickStartKey, 'true');
      setShowQuickStart(true);
    }
  };

  // 標記任務為已完成
  const markTaskCompleted = (taskKey: string) => {
    if (currentMerchant && !completedTasks.includes(taskKey)) {
      const newCompletedTasks = [...completedTasks, taskKey];
      setCompletedTasks(newCompletedTasks);

      const tasksKey = `${COMPLETED_TASKS_STORAGE_KEY}_merchant_${currentMerchant.id}`;
      localStorage.setItem(tasksKey, JSON.stringify(newCompletedTasks));
    }
  };

  // 標記任務完成並自動進入教學下一步
  const markTaskCompletedAndNext = (taskKey: string) => {
    markTaskCompleted(taskKey);
    // 如果正在進行教學，自動進入下一步
    if (showOnboarding) {
      nextStep();
    }
  };

  // 清除已完成任務（用於測試或重置）
  const clearCompletedTasks = () => {
    if (currentMerchant) {
      setCompletedTasks([]);
      const tasksKey = `${COMPLETED_TASKS_STORAGE_KEY}_merchant_${currentMerchant.id}`;
      localStorage.removeItem(tasksKey);
    }
  };

  const value: OnboardingContextValue = {
    showTutorialSelection,
    showOnboarding,
    selectedTutorial,
    currentStep,
    loading,
    startTutorial,
    skipAllTutorials,
    completeOnboarding,
    skipOnboarding,
    resetOnboarding,
    openTutorialSelection,
    nextStep,
    prevStep,
    goToStep,
    showQuickStart,
    hideQuickStart,
    displayQuickStart,
    completedTasks,
    markTaskCompleted,
    markTaskCompletedAndNext,
    clearCompletedTasks,
  };

  return (
    <OnboardingContext.Provider value={value}>
      {children}
    </OnboardingContext.Provider>
  );
};

/**
 * useOnboarding - 使用新手引導的 Hook
 *
 * 使用範例：
 * ```tsx
 * import { useOnboarding } from '@contexts/admin/OnboardingContext';
 *
 * function MyComponent() {
 *   const { showOnboarding, completeOnboarding } = useOnboarding();
 *
 *   return (
 *     <div>
 *       {showOnboarding && <OnboardingModal />}
 *     </div>
 *   );
 * }
 * ```
 */
export function useOnboarding(): OnboardingContextValue {
  const context = useContext(OnboardingContext);
  if (!context) {
    throw new Error('useOnboarding must be used within OnboardingProvider');
  }
  return context;
}

// Safe version that doesn't throw - returns default values when not in provider
export function useSafeOnboarding(): OnboardingContextValue | { showOnboarding: false } {
  const context = useContext(OnboardingContext);
  return context || { showOnboarding: false };
}
